import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ImageFile } from "../App";
import { EditModal } from "./EditModal";
import JSZip from "jszip";
import { saveAs } from "file-saver";

interface ImagesProps {
  images: ImageFile[];
  onDelete: (id: number) => void;
}

export function Images({ images, onDelete }: ImagesProps) {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isDownloading, setIsDownloading] = useState(false);
  
  const completedImages = images.filter(img => img.processedFile);
  const hasCompletedImages = completedImages.length > 0;
  
  const downloadAllImages = async () => {
    if (!hasCompletedImages) return;
    
    setIsDownloading(true);
    try {
      const zip = new JSZip();
      
      // 添加所有处理完成的图片到zip
      for (const image of completedImages) {
        if (image.processedFile) {
          const fileName = `${image.file.name.split('.')[0]}-bg-removed.png`;
          zip.file(fileName, await image.processedFile.arrayBuffer());
        }
      }
      
      // 生成并下载zip文件
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, "CuttyPic-batch-removed.zip");
    } catch (error) {
      console.error("Download error:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div>
      {images.length > 0 && (
        <div className="flex justify-between items-center mb-4">
          <div className="text-gray-700 font-medium">
            {t('images.processed')} <span className="text-pink-500">{completedImages.length}</span>/{images.length}
          </div>
          
          <div className="flex items-center gap-3">
            {/* 视图切换按钮 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-1 flex">
              <button 
                onClick={() => setViewMode('grid')}
                className={`px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors ${
                  viewMode === 'grid' 
                    ? 'bg-pink-50 text-pink-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Grid View"
              >
                <i className="fas fa-th-large"></i>
              </button>
              <button 
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-pink-50 text-pink-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="List View"
              >
                <i className="fas fa-list"></i>
              </button>
            </div>
            
            {/* 批量下载按钮 */}
            <button
              onClick={downloadAllImages}
              disabled={!hasCompletedImages || isDownloading}
              className={`px-4 py-2 rounded-full flex items-center gap-1.5 transition-colors ${
                hasCompletedImages && !isDownloading
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-sm hover:opacity-90'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isDownloading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>{t('images.processing')}</span>
                </>
              ) : (
                <>
                  <i className="fas fa-download"></i>
                  <span>{t('images.downloadAll')}</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
      
      {viewMode === 'grid' ? (
        <div className="gap-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {images.map((image) => {
            if(image.file.type.includes("video")) {
              return <Video video={image} key={image.id} />;
            } else {
              return <ImageSpot image={image} onDelete={onDelete} key={image.id} />;
            }
          })}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {images.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <i className="fas fa-image text-3xl mb-2 text-gray-300"></i>
              <p>{t('images.noImages')}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {images.map((image) => {
                if(image.file.type.includes("video")) {
                  return <VideoListItem video={image} key={image.id} />;
                } else {
                  return <ImageListItem image={image} onDelete={onDelete} key={image.id} />;
                }
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Video({ video }: { video: ImageFile }) {
  const url = URL.createObjectURL(video.file);
  return (
    <div className="bg-white rounded-lg shadow-md p-3">
      <video
        className="rounded-lg aspect-square object-cover"
        loop
        muted
        autoPlay
        src={url}
      ></video>
    </div>
  );
}

function VideoListItem({ video }: { video: ImageFile }) {
  const url = URL.createObjectURL(video.file);
  return (
    <div className="p-3 flex items-center gap-4">
      <div className="w-16 h-16 rounded-md overflow-hidden bg-gray-100 flex-shrink-0">
        <video
          className="w-full h-full object-cover"
          loop
          muted
          autoPlay
          src={url}
        ></video>
      </div>
      <div className="flex-grow">
        <p className="text-sm font-medium text-gray-700 truncate">{video.file.name}</p>
        <p className="text-xs text-gray-500">{formatFileSize(video.file.size)}</p>
      </div>
      <div className="text-gray-400">
        <i className="fas fa-film"></i>
      </div>
    </div>
  );
}

interface ImageSpotProps {
  image: ImageFile;
  onDelete: (id: number) => void;
}

function ImageSpot({ image, onDelete }: ImageSpotProps) {
  const { t } = useTranslation();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [processedImageUrl, setProcessedImageUrl] = useState("");

  const url = URL.createObjectURL(image.file);
  const processedURL = image.processedFile ? URL.createObjectURL(image.processedFile) : "";
  const isProcessing = !image.processedFile;

  const handleEditSave = (editedImageUrl: string) => {
    setProcessedImageUrl(editedImageUrl);
  };

  const transparentBg = `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQBAMAAADt3eJSAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAGUExURb+/v////5nD/3QAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAUSURBVBjTYwABQSCglEENMxgYGAAynwRB8BEAgQAAAABJRU5ErkJggg==")`;

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="relative">
        {isProcessing ? (
          <div className="relative">
            <img
              className="w-full aspect-square object-cover opacity-50 transition-opacity duration-200"
              src={url}
              alt={`Processing image ${image.id}`}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-black bg-opacity-50 px-4 py-2 rounded-lg">
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                <span className="text-white font-medium">{t('images.processing')}</span>
              </div>
            </div>
          </div>
        ) : (
          <div 
            className="w-full aspect-square"
            style={{ 
              background: transparentBg,
              backgroundRepeat: 'repeat'
            }}
          >
            <img
              className="w-full h-full object-cover transition-opacity duration-200"
              src={processedImageUrl || processedURL}
              alt={`Processed image ${image.id}`}
            />
          </div>
        )}
      </div>

      {!isProcessing && (
        <div className="p-3 border-t border-gray-100">
          <div className="flex justify-center gap-2">
            <button
              onClick={() => onDelete(image.id)}
              className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
              title="Delete"
            >
              <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span className="text-sm text-gray-700">{t('images.delete')}</span>
            </button>
            <button
              onClick={() => setIsEditModalOpen(true)}
              className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
              title="Edit"
            >
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              <span className="text-sm text-gray-700">{t('images.edit')}</span>
            </button>
            <a
              href={processedImageUrl || processedURL}
              download={`processed-${image.id}.png`}
              className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
              title="Download"
            >
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span className="text-sm text-gray-700">{t('images.download')}</span>
            </a>
          </div>
        </div>
      )}

      <EditModal
        image={image}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleEditSave}
      />
    </div>
  );
}

function ImageListItem({ image, onDelete }: ImageSpotProps) {
  const { t } = useTranslation();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [processedImageUrl, setProcessedImageUrl] = useState("");

  const url = URL.createObjectURL(image.file);
  const processedURL = image.processedFile ? URL.createObjectURL(image.processedFile) : "";
  const isProcessing = !image.processedFile;

  const handleEditSave = (editedImageUrl: string) => {
    setProcessedImageUrl(editedImageUrl);
  };

  const transparentBg = `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQBAMAAADt3eJSAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAGUExURb+/v////5nD/3QAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAUSURBVBjTYwABQSCglEENMxgYGAAynwRB8BEAgQAAAABJRU5ErkJggg==")`;

  return (
    <div className="p-3 flex items-center gap-4">
      <div 
        className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0"
        style={{ 
          background: isProcessing ? 'none' : transparentBg,
          backgroundRepeat: 'repeat'
        }}
      >
        {isProcessing ? (
          <div className="relative w-full h-full">
            <img
              className="w-full h-full object-cover opacity-50"
              src={url}
              alt={`Processing image ${image.id}`}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-pink-500"></div>
            </div>
          </div>
        ) : (
          <img
            className="w-full h-full object-cover"
            src={processedImageUrl || processedURL}
            alt={`Processed image ${image.id}`}
          />
        )}
      </div>
      
      <div className="flex-grow">
        <p className="text-sm font-medium text-gray-700 truncate">{image.file.name}</p>
        <p className="text-xs text-gray-500">{formatFileSize(image.file.size)}</p>
      </div>
      
      {!isProcessing && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => onDelete(image.id)}
            className="w-8 h-8 flex items-center justify-center rounded-full text-red-500 hover:bg-red-50 transition-colors"
            title="Delete"
          >
            <i className="fas fa-trash-alt text-sm"></i>
          </button>
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="w-8 h-8 flex items-center justify-center rounded-full text-blue-500 hover:bg-blue-50 transition-colors"
            title="Edit"
          >
            <i className="fas fa-edit text-sm"></i>
          </button>
          <a
            href={processedImageUrl || processedURL}
            download={`processed-${image.id}.png`}
            className="w-8 h-8 flex items-center justify-center rounded-full text-green-500 hover:bg-green-50 transition-colors"
            title="Download"
          >
            <i className="fas fa-download text-sm"></i>
          </a>
        </div>
      )}
      
      <EditModal
        image={image}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleEditSave}
      />
    </div>
  );
}

// 格式化文件大小的辅助函数
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
